import frLocale from '@fullcalendar/core/locales/fr';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import toFullCalendarAllDayEnd from 'src/Hooks/CalendarLogic/toFullCalendarAllDayEnd';
import useEventNavigation from 'src/Hooks/CalendarLogic/useEventNavigation';
import useFetchEvents from 'src/Hooks/Queries/useFetchEvents';
import { EventDtoOriginEnum, EventDtoTypeEnum } from 'src/Services/API/generated';
import eventTypeColors from 'src/Utils/eventTypeColors';
import { getEventVisual } from 'src/Utils/eventVisuals';

import './FullCalendar.scss';

import { Props } from './WeekCalendar.types';

const WeekCalendar = ({ selectedDate, onDateClick }: Props) => {
  const { i18n, t } = useTranslation();

  const { data: events } = useFetchEvents({
    startDate: selectedDate.locale(i18n.language).startOf('week').add(-1, 'day'),
    endDate: selectedDate.locale(i18n.language).endOf('week').add(1, 'day'),
  });

  const isOccurrence = useCallback(
    (eventId: string) => events?.find(event => event.id === eventId)?.isOccurrence,
    [events]
  );

  const transformedEvents = useMemo(() => {
    return events?.map(event => {
      const visual = getEventVisual(event);
      const forceAllDay =
        visual.kind === 'ribbon' || visual.kind === 'marker' || visual.kind === 'reminder';
      const allDay = forceAllDay || event.allDay;
      const end = event.endTime
        ? allDay
          ? toFullCalendarAllDayEnd(event.endTime)
          : event.endTime.toDate()
        : undefined;
      return {
        start: event.startTime.toDate(),
        end,
        allDay,
        title: visual.kind === 'ribbon' ? visual.label : event.name,
        type: event.type,
        id: event.localId,
        backgroundColor:
          visual.color ??
          event.household?.color ??
          eventTypeColors[event.type ?? EventDtoTypeEnum.Other]?.bgColor,
        textColor: eventTypeColors[event.type ?? EventDtoTypeEnum.Other]?.fgColor,
        display: 'block',
      };
    });
  }, [events]);

  const navigateForEvent = useEventNavigation();

  const getEventFromLocalId = useCallback(
    (localId: string) => events?.find(event => event.localId === localId),
    [events]
  );

  const calendar = useRef<FullCalendar>(null);

  useEffect(() => {
    calendar?.current?.getApi().setOption('locale', i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    calendar?.current
      ?.getApi()
      .gotoDate(selectedDate.locale(i18n.language).startOf('week').toDate());
  }, [calendar, i18n.language, selectedDate]);

  useEffect(() => {
    const now = dayjs();
    const startOfDay = now.startOf('day');
    const millisecondsSinceStartOfDay = now.diff(startOfDay);

    calendar?.current?.getApi().scrollToTime(millisecondsSinceStartOfDay);
  }, [calendar]);

  return (
    <>
      <FullCalendar
        ref={calendar}
        height={'100%'}
        plugins={[timeGridPlugin, interactionPlugin]}
        eventInteractive={true}
        initialView="timeGridWeek"
        scrollTimeReset={false}
        headerToolbar={{ left: '', center: '', right: '' }}
        allDaySlot={true}
        allDayText={t('planning.weekAllDay', 'Toute la journée')}
        slotMinTime="07:00:00"
        slotMaxTime="22:00:00"
        slotDuration="01:00:00"
        nowIndicator={true}
        events={transformedEvents}
        locale={i18n.language}
        locales={[frLocale]}
        dayHeaderFormat={{ weekday: 'short' }}
        dateClick={info => onDateClick?.(info.date)}
        eventClick={input => {
          const event = getEventFromLocalId(input.event.id);
          if (!event) return;
          if (event.origin === EventDtoOriginEnum.External) return;
          const occurrenceDate = isOccurrence(event.id) ? dayjs(input.event.start) : undefined;
          navigateForEvent(event, occurrenceDate);
        }}
      />
      <div id="weekCalendar" />
    </>
  );
};

export default WeekCalendar;
